import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  TemplateRef,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import { AccountInfo, RedirectRequest } from '@azure/msal-browser';
import { BdCompany } from '@bindoc-projects/resource-domain-lib';
import {
  BdCompanyContextService,
  BdGrantRequestsService,
} from '@bindoc-projects/bindoc-permissions-lib/data';
import { BdCompanyContextSelectorComponent } from '@bindoc-projects/bindoc-permissions-lib/ui';
import { BdAuthenticationService } from '@bindoc-projects/security-lib/data';
import { AvatarModule } from 'primeng/avatar';
import { ButtonDirective, ButtonIcon, ButtonLabel } from 'primeng/button';
import { Popover } from 'primeng/popover';
import {
  catchError,
  combineLatest,
  map,
  Observable,
  of,
  startWith,
  switchMap,
} from 'rxjs';

import { BdCurrentUserService } from '../current-user';
import { OverlayBadge } from 'primeng/overlaybadge';
import { Badge } from 'primeng/badge';

interface UserMenuState {
  accountInfo: AccountInfo | null;
  selectedCompany: BdCompany | undefined;
  loggedIn: boolean;
  inboxCount: number;
}

@Component({
  selector: 'bd-app-user-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (state$ | async; as state) {
      @if (state.loggedIn || preferences()) {
        <button
          pButton
          class="p-button-appShell text-left"
          style="height: 50px"
          [attr.aria-label]="state.accountInfo?.name || 'Konto und Darstellung'"
          aria-haspopup="dialog"
          data-test="user-menu-trigger"
          (click)="popover.toggle($event)"
        >
          <!-- On <sm only the avatar shows (with inbox badge overlaid when
               applicable) so it fits next to the other toolbar icons on
               phone-sized viewports. On >=sm the full name + company text
               plus a side-by-side avatar is rendered. The plain <span>
               wrappers own the responsive display so PrimeNG component
               host CSS (display: inline-flex on .p-avatar) doesn't override
               Tailwind's "hidden" on the inner element. -->
          <span class="inline-flex sm:hidden">
            @if (state.inboxCount > 0) {
              <p-overlay-badge
                [value]="'' + state.inboxCount"
                severity="danger"
              >
                <p-avatar
                  [label]="getInitials(state.accountInfo)"
                  shape="circle"
                  size="normal"
                />
              </p-overlay-badge>
            } @else {
              <p-avatar
                [label]="getInitials(state.accountInfo)"
                shape="circle"
                size="normal"
              />
            }
          </span>
          <span class="hidden sm:flex items-center">
            <span class="flex flex-col leading-tight">
              <span
                pButtonLabel
                class="text-sm font-medium flex items-center gap-1"
              >
                {{ state.accountInfo?.name || 'Konto' }}
                @if (state.inboxCount > 0) {
                  <p-badge [value]="'' + state.inboxCount" severity="danger" />
                }
              </span>
              @if (state.selectedCompany) {
                <span class="text-xs opacity-75">{{
                  state.selectedCompany.name
                }}</span>
              }
            </span>
            <p-avatar
              [label]="getInitials(state.accountInfo)"
              shape="circle"
              size="normal"
              class="ml-2 mr-2"
            />
          </span>
        </button>
      } @else {
        <button
          pButton
          class="p-button-appShell"
          data-test="user-menu-login"
          (click)="login()"
        >
          <i class="pi pi-sign-in mr-2" pButtonIcon></i>
          <span pButtonLabel>Anmelden</span>
        </button>
      }
    }

    <p-popover #popover appendTo="body">
      <div
        class="flex flex-col gap-3 w-72"
        style="max-width: calc(100vw - 3rem)"
      >
        @if (state$ | async; as state) {
          <div class="flex items-center gap-3 px-2 pt-1">
            <p-avatar
              [label]="getInitials(state.accountInfo)"
              shape="circle"
              size="large"
            />
            <div>
              <div class="font-semibold">
                {{ state.accountInfo?.name || 'Konto' }}
              </div>
              <div class="text-sm" style="color: var(--p-text-muted-color)">
                {{ getEmail(state.accountInfo) }}
              </div>
            </div>
          </div>
        }

        <ng-container [ngTemplateOutlet]="preferences()" />

        @if ((state$ | async)?.loggedIn) {
          <bd-company-context-selector></bd-company-context-selector>

          <div class="flex flex-col gap-1">
            @if (showNotifications()) {
              <a
                pButton
                outlined
                severity="secondary"
                routerLink="/user/notifications"
                class="w-full justify-start"
                (click)="popover.hide()"
              >
                @if (state$ | async; as s) {
                  @if (s.inboxCount > 0) {
                    <p-overlay-badge
                      [value]="'' + s.inboxCount"
                      severity="danger"
                    >
                      <i class="pi pi-bell mr-2" pButtonIcon></i>
                    </p-overlay-badge>
                  } @else {
                    <i class="pi pi-bell mr-2" pButtonIcon></i>
                  }
                }

                <span pButtonLabel>Benachrichtigungen</span>
              </a>
            }
            <button
              pButton
              outlined
              severity="secondary"
              (click)="logout(); popover.hide()"
            >
              <i class="pi pi-sign-out mr-2" pButtonIcon></i>
              <span pButtonLabel>Abmelden</span>
            </button>
            <button
              pButton
              outlined
              severity="secondary"
              (click)="passwordReset(); popover.hide()"
            >
              <span pButtonLabel>Passwort ändern</span>
            </button>
          </div>
        } @else {
          <button
            pButton
            severity="secondary"
            data-test="user-menu-login"
            (click)="login(); popover.hide()"
          >
            <i class="pi pi-sign-in" pButtonIcon></i>
            <span pButtonLabel>Anmelden</span>
          </button>
        }
      </div>
    </p-popover>
  `,
  imports: [
    AsyncPipe,
    NgTemplateOutlet,
    RouterLink,
    AvatarModule,
    ButtonDirective,
    ButtonIcon,
    ButtonLabel,
    Popover,
    BdCompanyContextSelectorComponent,
    OverlayBadge,
    Badge,
  ],
})
export class BdAppUserMenuComponent {
  private readonly _currentUserService = inject(BdCurrentUserService);
  private readonly _authService = inject(BdAuthenticationService);
  private readonly _msalService = inject(MsalService);
  private readonly _companyContext = inject(BdCompanyContextService);
  private readonly _grantRequestsService = inject(BdGrantRequestsService);
  private readonly _msalGuardConfig =
    inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);

  /** Optional app-owned preferences, also accessible before signing in. */
  public readonly preferences = input<TemplateRef<unknown> | null>(null);

  /**
   * Whether to show the notification badge and the "Benachrichtigungen"
   * link. Disable it in apps that do not register the `/user/notifications`
   * route (everything except the meta apps) so the link does not point at a
   * dead route. When disabled the grant-requests inbox polling is skipped too.
   */
  public readonly showNotifications = input(true);

  public readonly state$: Observable<UserMenuState> = combineLatest([
    this._currentUserService.currentUser,
    this._companyContext.selectedCompany$,
    this._authService.authentication$,
  ]).pipe(
    switchMap(([accountInfo, selectedCompany, loggedIn]) => {
      if (!loggedIn || !this.showNotifications()) {
        return of({ accountInfo, selectedCompany, loggedIn, inboxCount: 0 });
      }
      // The inbox count is a best-effort badge and must never gate the
      // visibility of the account widget. `inboxCount$` only emits once its
      // `/inbox` HTTP call resolves, so seed it with 0 to render the menu
      // immediately, and fall back to 0 if the request errors — otherwise a
      // failed/slow inbox request would leave signed-in users with no account
      // name and no login/logout controls at all.
      return this._grantRequestsService.inboxCount$.pipe(
        startWith(0),
        catchError(() => of(0)),
        map((inboxCount) => ({
          accountInfo,
          selectedCompany,
          loggedIn,
          inboxCount,
        })),
      );
    }),
  );

  public getInitials(accountInfo: AccountInfo | null): string {
    if (!accountInfo?.name) {
      return '?';
    }
    const parts = accountInfo.name.trim().split(/\s+/);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
  }

  public getEmail(accountInfo: AccountInfo | null): string {
    const claims = accountInfo?.idTokenClaims as
      { emails?: string[] } | undefined;
    return claims?.emails?.[0] ?? accountInfo?.username ?? '';
  }

  public login(): void {
    if (this._msalGuardConfig.authRequest) {
      this._msalService.loginRedirect({
        ...this._msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this._msalService.loginRedirect();
    }
  }

  public logout(): void {
    this._msalService.logoutRedirect();
  }

  public passwordReset(): void {
    this._msalService.loginRedirect({
      ...this._authService.getPasswordResetPolicy(),
      scopes: [],
    });
  }
}
